import React, { useState, useEffect } from 'react';
import { RotateCcw, ChevronDown, ChevronRight, Camera, ExternalLink, Image, Copy, Check } from 'lucide-react';
import { API_URL } from '../../config';

export default function PhotoLogAdmin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [expandedPrompt, setExpandedPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState({});
  const [copied, setCopied] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('snapshot_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadPhotoLog = async (newOffset = 0) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/photo-log?limit=${limit}&offset=${newOffset}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setOffset(newOffset);
      }
    } catch (e) {
      console.error('Error loading photo log:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPhotoLog(0);
  }, []);

  const getStatusBadge = (status) => {
    if (status === 'completed') return <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-400">completed</span>;
    if (status === 'failed') return <span className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400">failed</span>;
    if (status === 'processing') return <span className="px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400">processing</span>;
    return <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">{status}</span>;
  };

  const getTypeBadge = (type) => {
    if (type === 'Remix') return <span className="px-2 py-0.5 text-xs font-bold rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">Remix</span>;
    return <span className="px-2 py-0.5 text-xs font-bold rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Shot</span>;
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Camera className="w-7 h-7 text-emerald-400" />
            Foto Log
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Prompt-Verlauf aller Foto-Generierungen mit User-Prompt, Regisseur-Prompt und Ergebnissen.
          </p>
        </div>
        <button
          onClick={() => loadPhotoLog(0)}
          disabled={loading}
          className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition-all flex items-center gap-2"
        >
          <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Photo Log Entries */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <button
          onClick={() => setOpen(!open)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-all"
        >
          <div className="flex items-center gap-3">
            <Camera className="w-5 h-5 text-emerald-400" />
            <span className="text-lg font-semibold text-white">Foto-Generierungen</span>
            <span className="text-sm text-white/40">{total} Fotos gesamt</span>
          </div>
          {open ? <ChevronDown className="w-5 h-5 text-white/40" /> : <ChevronRight className="w-5 h-5 text-white/40" />}
        </button>

        {open && (
          <div className="px-6 pb-6 space-y-3">
            {loading && entries.length === 0 ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
              </div>
            ) : entries.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-6">Keine Foto-Einträge gefunden.</p>
            ) : (
              <div className="space-y-2">
                {entries.map((entry, idx) => {
                  const globalIdx = offset + idx;
                  return (
                    <div key={globalIdx} className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-2">
                      {/* Header Row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {entry.generation_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(entry.generation_id);
                              setCopied(`gid_${globalIdx}`);
                              setTimeout(() => setCopied(null), 1500);
                            }}
                            className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-white/5 text-white/30 hover:text-white/60 hover:bg-white/10 border border-white/10 transition-all cursor-pointer"
                            title={`Gen-ID: ${entry.generation_id}\nKlick zum Kopieren`}
                          >
                            {copied === `gid_${globalIdx}` ? 'Kopiert!' : `#${entry.generation_id.slice(0, 8)}`}
                          </button>
                        )}
                        <span className="text-white/40 text-xs font-mono">
                          {entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '\u2014'}
                        </span>
                        {getTypeBadge(entry.gen_type)}
                        {getStatusBadge(entry.status)}
                        {entry.image_format && (
                          <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/50 border border-white/10 font-mono">
                            {entry.image_format}
                          </span>
                        )}
                        {entry.style && entry.style !== 'none' && (
                          <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/40">
                            {entry.style}
                          </span>
                        )}
                        {entry.cost_snaps > 0 && (
                          <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-400">
                            {entry.cost_snaps} SNAPs
                          </span>
                        )}
                        <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/30">
                          {entry.image_count} {entry.image_count === 1 ? 'Bild' : 'Bilder'}
                        </span>
                        {/* Provider-Badge: zeigt das tatsaechlich generierende Modell.
                            Bei fallback_provider="astria" hat gpt-image-2 wegen Safety
                            geblockt und der Astria-Tune ist eingesprungen. */}
                        {entry.fallback_provider === 'astria' ? (
                          <span
                            className="px-2 py-0.5 text-[10px] font-bold rounded bg-amber-500/20 text-amber-400 border border-amber-500/40"
                            title={`Fallback auf Astria/Flux — Grund: ${entry.fallback_reason || 'safety_violations'}`}
                          >
                            FLUX ({entry.fallback_reason || 'safety'})
                          </span>
                        ) : entry.pipeline === 'gpt-image-2' ? (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            GPT-IMAGE-2
                          </span>
                        ) : entry.pipeline ? (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                            ASTRIA
                          </span>
                        ) : null}
                        <span className="text-white/20 text-xs ml-auto">User: {entry.user_id} | Avatar: {entry.avatar_id}</span>
                      </div>

                      {/* Flow-Badge */}
                      {entry.workflow_flow && (
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            Flow {entry.workflow_flow}
                          </span>
                          {entry.workflow_cost > 0 && (
                            <span className="text-white/30 text-[10px]">
                              ${entry.workflow_cost.toFixed(4)} Workflow
                            </span>
                          )}
                        </div>
                      )}

                      {/* Pipeline-Tabs — abhängig von der genutzten Pipeline:
                          - gpt-image-2 (Snapshot 3.0): User → Bildregisseur → Final → Settings → Error
                          - Astria-Legacy: User → Router → Bildanalyst → Bildregisseur → Flux Designer → Compositor → Provider → Final */}
                      {(() => {
                        const tab = activeTab[globalIdx] || 'user';
                        const isGptImage2 = entry.pipeline === 'gpt-image-2';

                        // Settings-Block (für gpt-image-2): kompaktes JSON mit den relevanten UI-Werten
                        const settingsBlock = isGptImage2 ? {
                          model: entry.model || 'gpt-image-2',
                          size: entry.size,
                          quality: entry.quality,
                          image_format: entry.image_format,
                          camera: entry.camera,
                          body_framing: entry.body_framing,
                          reference_mode: entry.reference_mode,
                          has_user_reference: entry.has_user_reference,
                          director_cost_usd: entry.director_cost_usd,
                        } : null;

                        const tabs = isGptImage2 ? [
                          { id: 'user',     label: 'User-Prompt',   color: 'emerald', content: entry.user_prompt },
                          { id: 'director', label: 'Bildregisseur', color: 'purple',  content: entry.director_output },
                          { id: 'final',    label: 'Final Prompt (an gpt-image-2)', color: 'rose', content: entry.final_prompt },
                          // Nur sichtbar, wenn der Astria-Fallback eingesprungen ist
                          { id: 'flux',     label: 'Final Prompt (an Flux)', color: 'amber', content: entry.fallback_flux_prompt },
                          { id: 'settings', label: 'Settings',      color: 'teal',    content: settingsBlock },
                          { id: 'error',    label: 'Fehler',        color: 'pink',    content: entry.error_message },
                        ] : [
                          { id: 'user', label: 'User-Prompt', color: 'emerald', content: entry.user_prompt },
                          { id: 'router', label: 'Router', color: 'cyan', content: entry.agent_router },
                          { id: 'analyst', label: 'Bildanalyst', color: 'blue', content: entry.agent_bildanalyst },
                          { id: 'director', label: 'Bildregisseur', color: 'purple', content: entry.agent_bildregisseur },
                          { id: 'flux', label: 'Flux Designer', color: 'amber', content: entry.agent_flux_designer },
                          { id: 'compositor', label: 'Compositor', color: 'pink', content: entry.agent_compositor },
                          { id: 'providers', label: 'Provider', color: 'teal', content: entry.agent_providers },
                          { id: 'final', label: 'Final Prompt', color: 'rose', content: entry.final_prompt },
                        ];
                        const current = tabs.find(t => t.id === tab) || tabs[0];
                        const isJson = current.content && typeof current.content === 'object';
                        const displayContent = isJson ? JSON.stringify(current.content, null, 2) : (current.content || '—');
                        const isExpanded = expandedPrompt === `tab_${globalIdx}`;

                        return (
                          <div className="space-y-1">
                            <div className="flex gap-1 flex-wrap">
                              {tabs.map(t => {
                                const hasContent = t.content && (typeof t.content === 'string' ? t.content.trim() : Object.keys(t.content).length > 0);
                                const colorMap = {
                                  emerald: tab === t.id ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : '',
                                  blue: tab === t.id ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : '',
                                  purple: tab === t.id ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : '',
                                  rose: tab === t.id ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' : '',
                                  cyan: tab === t.id ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : '',
                                  amber: tab === t.id ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : '',
                                  pink: tab === t.id ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' : '',
                                  teal: tab === t.id ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : '',
                                };
                                return (
                                  <button
                                    key={t.id}
                                    onClick={() => setActiveTab(prev => ({ ...prev, [globalIdx]: t.id }))}
                                    className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-t-lg transition-all border border-b-0 ${
                                      tab === t.id
                                        ? colorMap[t.color]
                                        : hasContent
                                          ? 'bg-white/5 text-white/40 hover:text-white/60 border-white/10'
                                          : 'bg-white/5 text-white/20 border-white/5 opacity-50'
                                    }`}
                                    disabled={!hasContent}
                                  >
                                    {t.label}
                                    {hasContent && <span className="ml-1 text-[8px]">●</span>}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="bg-black/30 border border-white/10 rounded-lg rounded-tl-none px-3 py-2 relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(displayContent);
                                  setCopied(`tab_${globalIdx}`);
                                  setTimeout(() => setCopied(null), 1500);
                                }}
                                className="absolute top-1.5 right-1.5 p-1 rounded bg-white/5 hover:bg-white/15 text-white/40 hover:text-white/80 transition-all z-10"
                                title="Kopieren"
                              >
                                {copied === `tab_${globalIdx}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                              <div
                                className="cursor-pointer pr-6"
                                onClick={() => setExpandedPrompt(isExpanded ? null : `tab_${globalIdx}`)}
                              >
                                <pre className={`text-white/70 text-xs font-mono whitespace-pre-wrap break-words ${isExpanded ? '' : 'line-clamp-3'}`}>
                                  {displayContent}
                                </pre>
                                {displayContent.length > 200 && !isExpanded && (
                                  <span className="text-white/30 text-[10px]">Klicken zum Aufklappen...</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Error message */}
                      {entry.status === 'failed' && entry.error_message && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                          <p className="text-red-400 text-xs font-mono">{entry.error_message}</p>
                        </div>
                      )}

                      {/* Ergebnis-Bilder */}
                      {entry.image_urls && entry.image_urls.length > 0 && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {entry.image_urls.map((url, imgIdx) => (
                            <a key={imgIdx} href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={url}
                                alt={`result ${imgIdx + 1}`}
                                className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-emerald-500/50 transition-all"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </a>
                          ))}
                          {/* Remix-Quelle */}
                          {entry.remix_source && (
                            <a href={entry.remix_source} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30 rounded-lg transition-all"
                            >
                              <Image className="w-3 h-3" />
                              Remix-Quelle
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-white/40 text-sm">
                  {offset + 1}–{Math.min(offset + limit, total)} von {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadPhotoLog(Math.max(0, offset - limit))}
                    disabled={offset === 0 || loading}
                    className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg disabled:opacity-30 transition-all"
                  >
                    Zurück
                  </button>
                  <button
                    onClick={() => loadPhotoLog(offset + limit)}
                    disabled={offset + limit >= total || loading}
                    className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg disabled:opacity-30 transition-all"
                  >
                    Weiter
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
